import { Injectable } from '@angular/core';
import { TrainingLevel } from '@cf-shared/data-model/training-level';
import { IntensityLevel } from '@cf-shared/data-model/fitness-plan';
import { AuthRole } from '@cf-shared/couch-auth';
import { LocaleSettings, parseJSONSafely } from '@cf-shared/shared-util';
import { DateTime } from 'luxon';
import {
  assertUsableReviewPromptMoment, mergeReviewPromptAskLists, mergeReviewPromptTimestamp, readReviewPromptAsks,
  readReviewPromptTimestamp,
} from './review-prompt-settings';
import { isDatabaseTornDown } from './database-unavailable-error';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { NutritionLabelStyle, DateFormat } from '@cf-shared/enums';
import type { Locales } from '../../gensrc/i18n/i18n-types';

export enum ClockHourMode {
  _12hr = '12hr',
  _24hr = '24hr',
  Automatic = 'auto',
}

export enum FoodValuesDisplayMode {
  Serving = 'serving',
  Per100g = '100g',
}

export enum ScrollHideMode {
  Always = 'always',
  Never = 'never',
  Auto = 'auto',
}

export enum FirstDayOfWeek {
  Monday = 'monday',
  Saturday = 'saturday',
  Sunday = 'sunday',
  Automatic = 'auto',
}

export const programSettingKeys: ReadonlyArray<string> = [
  'nutritionLabelStyle',
  'foodValuesDisplayMode',
  'unitsPrefsJSON',
];

/** How `saveInBackground` reports a save that failed for a reason other than our own
 * teardown. `LoggingService.trackSilentProblem` is what really answers this; it is typed
 * structurally so `settings.service.ts` need not import the logging service. */
export type SettingsSaveFailureReporter = (problemName: string, context: any) => void;

@Injectable()
export class SettingsService {
  public _defaults = {};
  public _settingsLoaded = false; // safety for refreshing pages when debugging
  public _isManagingClient = false;

  // The user's chosen UI language (a supported locale id: 'en'|'es'|'nl'|'fr',
  // or '' when never chosen). As a `_defaults` field it auto-persists to the
  // localStorage `settings` blob and auto-syncs to the CouchDB `settings`
  // singleton — the same store as the weight-unit / regional prefs, so no extra
  // plumbing. This is the AUTHORITATIVE, cross-device preference. The synchronous
  // boot-time copy that drives module-load is the device-scoped `cs_ui_language`
  // key (see ../i18n/ui-language.ts); this per-user value wins on sign-in via the
  // reconciliation in app.component.ts. See design-docs/i18n-translation.md.
  public uiLanguage: Locales | '' = '';

  public clockHourMode = ClockHourMode.Automatic;
  public nutritionLabelStyle: NutritionLabelStyle = undefined;
  public dateFormat: DateFormat = DateFormat.Automatic;
  public firstDayOfWeek: FirstDayOfWeek = FirstDayOfWeek.Automatic;

  public cardAnimations = true;
  public keyboardLastView: 'keyboard' | 'roller' | undefined = undefined; // Remember last selected keyboard view mode

  public testMode: boolean = false;
  /** Generate only one exercise (squats) per workout for all workouts. Use this for progression loop testing. */
  public progressionTesting = false;
  public progressionTesting_Slot = '';
  public progressionTesting_Sets = undefined;
  public progressionTesting_Level = TrainingLevel.Intermediate;
  public progressionTesting_otherLevel = TrainingLevel.Intermediate;
  public progressionTesting_Intensity = IntensityLevel.DUPheavy;
  public progressionTesting_SlotIntensities: string = '';

  public debugFoodSearch = false; // show extra information about how matches were scored
  public foodValuesDisplayMode: FoodValuesDisplayMode = FoodValuesDisplayMode.Serving; // show food values by serving or per 100g
  public showSlotName = false; // show exercise slot name (muscle group) in workout and exercise configuration

  public cachedRole = AuthRole.Unknown;
  public cachedRoleExpiry_utc = 'never';
  public unitsPrefsJSON: string = ''; // this stores per-measurement-type default unit chosen by the user
  public hintsSeenJSON: string = ''; // this stores which one-time-hints user has already seen
  public helpSeenJSON: string = ''; // this stores which help pages user has already seen
  public permanentlyDismissedToastsJSON: string = ''; // this stores which toasts user has permanently dismissed
  public debugShowAllHints: boolean = false;
  public debugShowAllTutorials: boolean = false;
  // allow the native-only rest-timer UI (pill + expanded overlay + service)
  // to run on PWA / desktop so it can be exercised outside of the native apps
  public debugRestTimerOnWeb: boolean = false;
  public locale: LocaleSettings = {};

  public currentWOSolverTestId: string = ''; // used by the WO solver test page

  public usedCommunityBefore = false;

  // Sticky "this user has logged food at least once" marker (#4325). Set by
  // TelemetryService the first time it sees one of the FOOD_LOGGED_EVENTS, and
  // never cleared. It becomes the `has_logged_food_ever` Refiner trait, which
  // lets marketing target a live survey segment ("paying users who never logged
  // food") instead of the hand-synced Mixpanel snapshot they use today.
  //
  // As a `_defaults` field it auto-persists to the localStorage `settings` blob
  // AND auto-syncs to the CouchDB `settings` singleton, so the marker follows the
  // user to a new device with no extra plumbing — same as usedCommunityBefore
  // above. That singleton is last-writer-wins, so a device that has not synced
  // yet could in principle write `false` back over a `true`; the user's next
  // food-log event flips it straight back, which is fine for a targeting flag.
  public hasLoggedFoodEver = false;

  // #4625 — the two review-prompt moments. Both are ISO timestamps written in
  // UTC, and both are `''` until the moment has happened.
  //
  // As `_defaults` fields they persist to the localStorage `settings` blob AND
  // sync to the CouchDB `settings` singleton, so the cap survives a reinstall
  // and follows the user to a new device — which is exactly what a once-a-year
  // cap needs. **Unlike every other field here they are NOT copied plainly in
  // either direction.** That singleton is last-writer-wins, so a device that has
  // not synced yet could write a blank back over a real date; for
  // `hasLoggedFoodEver` above that is harmless because the next food-log event
  // flips it straight back, but a lost cap marker means the person is asked to
  // rate the app a second time and nothing reports it. Both the load-side and
  // the save-side copies in `settings-sync-copy.ts` route these two keys through
  // `mergeReviewPromptTimestamp`, which keeps the later moment and never lets a
  // blank win. Adding another such field means registering its rule in that
  // file's `findSettingsMergeRule`, once, and both directions then apply it.
  // See `review-prompt-settings.ts` and
  // design-docs/review-prompts.md, "The cap, and where it lives".

  /** When we last asked this person to rate the app. Drives the 365-day cap. */
  public reviewPromptLastAskedAt_utc: string = '';
  /** When they last told us something was wrong, from either reporting route. */
  public reviewPromptLastProblemReportedAt_utc: string = '';
  /**
   * #4816 — every time we asked, oldest first, for phase 2's two-a-year cap. A
   * real array, merged as a union in both copy directions. Never mutated in
   * place: `_defaults` holds the same array object this starts as.
   * `reviewPromptLastAskedAt_utc` above is still written beside it, because a
   * device running phase 1 reads only that one.
   */
  public reviewPromptAskedAt_utc: string[] = [];

  // #4963 phase 3 — the two referral markers. Both are sticky: once true they
  // never go back to false, because both describe something that has happened
  // and cannot un-happen. **Like the two review-prompt fields above they are NOT
  // copied plainly in either direction** — both copies in `settings-sync-copy.ts`
  // route these keys through `mergeStickyReferralFlag`. See
  // `referral-settings.ts`, which says why, and design-docs/referral-program.md.

  /** Somebody invited this user and their claim succeeded. Arms the training report. */
  public referralWasReferred = false;
  /** The server has confirmed this user's training flag. Stops this device asking again. */
  public referralTrainingReported = false;

  // How often this user takes each route into a meal, as JSON: one decayed score per route
  // id (#4969). It orders the "Add to this meal" routes on the meal screen, so the routes
  // this person actually uses are the ones the folded strip has room to show.
  //
  // As a `_defaults` field it persists to the localStorage `settings` blob AND syncs to the
  // CouchDB `settings` singleton, so the ordering follows the user to a new device -- which
  // is what the product owner asked for: a preference about their own habits, not a
  // property of one phone. Ordinary last-writer-wins is right here, unlike the review-prompt
  // and referral fields above: nothing about it is a record that something HAPPENED, and a
  // lost update costs at most a few taps' worth of ordering, which the next few taps rebuild.
  //
  // Every read goes through `readRouteUsage` in
  // `pages/edit-meal/add-route-usage.ts`, which treats the stored text as untrusted.
  public mealAddRouteUsageJSON: string = '';

  public showReportBugFAB2 = false;

  // Debug log - viewable in test settings
  public debugLog: string = '';

  // the user-label of the last used camera exposed by the API - used to use the same camera across app restarts
  public barcodeCameraId = '';
  public barcodeCameraZoom: number = null;
  // we track whether the known camera is functional, so we don't need to enumerate cameras for eventual toggling
  public barcodeCameraFunctional = false;


  // last model the user picked in any scanner's 'any'-mode picker (#3783 —
  // one shared choice across all scanners). Renamed from the old
  // label-only `labelScanModel`; loadFromLocalStorage reads that old key once
  // as a fallback so existing installs keep their previously-chosen model.
  public scanModel = '';

  public showDayStartEvents = true;
  public showDayEndEvents = true;

  // Homepage customization settings
  public homepageShowWeighIn = true;
  public homepageShowWorkout = true;
  public homepageShowMeals = true;
  public homepageShowGoalCard = true;
  public homepageShowBodyComposition = true;
  public homepageShowNutritionChart = true;
  public homepageShowCoachingTips = true;

  public headerScrollHide: ScrollHideMode = ScrollHideMode.Auto;
  public footerScrollHide: ScrollHideMode = ScrollHideMode.Never;

  // notification preferences (local push notifications on native apps)
  public notificationsWorkout: boolean = true;
  public notificationsMeals: boolean = true;
  public notificationsWeighIn: boolean = true;
  public notificationsWeeklyCheckup: boolean = true;
  public notificationsSleep: boolean = true;

  constructor() {
    for (const k of Object.keys(this)) {
      if (!k.startsWith('_')) {
        this._defaults[k] = this[k];
      }
    }

    Object.seal(this);
  }

  async loadSettingsAsync(): Promise<any> {
    // load from localStorage
    await this.loadFromLocalStorage();

    if (this.clockHourMode === undefined) {
      this.clockHourMode = ClockHourMode.Automatic;
    }
    console.log('Settings: loaded:', this);
    this._settingsLoaded = true;
  }

  private async loadFromLocalStorage(): Promise<void> {
    // load all settings from a single JSON object in localStorage
    try {
      const settingsJson = localStorage.getItem('settings');
      if (settingsJson) {
        const settings = JSON.parse(settingsJson);
        for (const k in this._defaults) {
          if (k in settings) {
            this[k] = settings[k];
          }
        }
        // one-time migration (#3783): the scanner model choice was renamed
        // labelScanModel → scanModel. If this install has no scanModel yet but
        // has the old labelScanModel stored, carry it over so the user keeps
        // their previously-picked model. The old key is no longer a field, so
        // it is read straight from the parsed JSON and dropped on next save.
        if (!this.scanModel && typeof settings['labelScanModel'] === 'string') {
          this.scanModel = settings['labelScanModel'];
        }
      }
    } catch (e) {
      // every preference on this device silently falls back to its default, so this must
      // reach a debugger (#5191). the reporter is installed in app.component's constructor,
      // before this load runs.
      this.reportProblem('SettingsLocalLoadFailed', { error: describeErrorForLog(e) });
    }
  }

  isLoaded() {
    return this._settingsLoaded;
  }

  // return the user's chosen unit id for a single measurement type (e.g. 'bodyweight_kg' → 'kg' or 'lb').
  // written by cs-input-measure when the user toggles a unit or enters a first value; returns undefined
  // when no choice has been persisted yet, when the stored json is malformed, or when it decodes to
  // something other than a plain object (matches the object-shape guard in user-db.migrations).
  getUnitPref(type: string): string | undefined {
    const prefs = parseJSONSafely<Record<string, string>>(this.unitsPrefsJSON);
    if (!prefs || typeof prefs !== 'object' || Array.isArray(prefs)) return undefined;
    return prefs[type] || undefined;
  }

  // returns ISO weekday indices in display order
  // Monday: [0,1,2,3,4,5,6], Saturday: [5,6,0,1,2,3,4], Sunday: [6,0,1,2,3,4,5]
  getWeekDayDisplayOrder(): number[] {
    let startDay: number; // 1=Mon, 6=Sat, 7=Sun
    if (this.firstDayOfWeek === FirstDayOfWeek.Monday) {
      startDay = 1;
    } else if (this.firstDayOfWeek === FirstDayOfWeek.Saturday) {
      startDay = 6;
    } else if (this.firstDayOfWeek === FirstDayOfWeek.Sunday) {
      startDay = 7;
    } else {
      // Automatic: use detected locale setting, default to Monday
      startDay = this.locale?.weekStartDay ?? 1;
    }

    // convert to ISO index (Mon=0, Sat=5, Sun=6)
    const isoStart = startDay === 7 ? 6 : startDay - 1;
    // rotate array to start from isoStart
    const order: number[] = [];
    for (let i = 0; i < 7; i++) {
      order.push((isoStart + i) % 7);
    }
    return order;
  }

  private _syncCallback: ((settings: SettingsService) => Promise<void>) | null = null;

  // null detaches it: sign-out does that, so no later save can write into the signed-out user's database
  setSyncCallback(callback: ((settings: SettingsService) => Promise<void>) | null) {
    this._syncCallback = callback;
  }

  private _saveFailureReporter: SettingsSaveFailureReporter | null = null;

  /**
   * Give the background save somewhere to report a real failure to.
   *
   * Handed over rather than injected, the same shape as `setSyncCallback` above and for the
   * same reason: this service is built with no constructor arguments — the unit test does
   * `new SettingsService()` — so taking `LoggingService` through the constructor would pull
   * Angular into everything that builds one. `app.component.ts` installs it during boot.
   */
  setSaveFailureReporter(reporter: SettingsSaveFailureReporter) {
    this._saveFailureReporter = reporter;
  }

  /**
   * Report a problem with this device's stored settings through the reporter above. Before one
   * is installed, say something rather than nothing — the same fallback `saveInBackground` makes.
   */
  private reportProblem(problemName: string, context: { error: string }) {
    if (this._saveFailureReporter) {
      this._saveFailureReporter(problemName, context);
      return;
    }
    console.error(`Settings: ${problemName} before logging was ready:`, context.error);
  }

  /**
   * Save without waiting, for the many callers that change one preference and carry straight
   * on. Use this rather than starting `saveAsync` with a bare `void` and nothing catching
   * the rejection (df-0778ad3a63d7).
   *
   * A `void`-ed promise that rejects reaches the global `unhandledrejection` handler, and
   * since `4021db796` a save CAN reject: `saveRecordAsync` throws when the database is being
   * torn down. So a preference written a moment after sign-out, Delete Private Data or an
   * account switch used to be filed as `DatabaseUnavailableUnhandled` with a `console.error`
   * line — a Sentry record for something that is not a fault, and a failure of any E2E
   * scenario that crosses it.
   *
   * Our own teardown is expected here and is ignored: the values are already in this
   * device's `localStorage` by the time the database copy is attempted, and they are written
   * again the next time they change. Every OTHER failure is still reported.
   *
   * `callSite` names where the save came from in a word or two, so a report says which
   * preference save failed. See `design-docs/pass-through-db.md`.
   */
  saveInBackground(callSite: string): void {
    void this.saveAsync().catch(err => {
      // our own teardown: deliberately console.log and not console.error, so this cannot
      // trip the E2E unexpected-browser-error gate
      if (isDatabaseTornDown(err)) {
        console.log(`Settings: the background save from "${callSite}" was refused by our own teardown; the values are already on this device.`);
        return;
      }
      if (this._saveFailureReporter) {
        this._saveFailureReporter('settings-background-save-failed', {
          callSite,
          error: describeErrorForLog(err),
        });
        return;
      }
      // no reporter yet — early boot, before app.component installed one. Say something
      // rather than nothing, the same fallback `reportDatabaseUnavailableError` makes.
      console.error(`Settings: the background save from "${callSite}" failed before logging was ready:`, describeErrorForLog(err));
    });
  }

  async saveAsync(skipDatabaseSync: boolean = false) {
    const settingsToSave = {};
    for (const k in this._defaults) {
      settingsToSave[k] = this[k];
    }

    // during management, preserve coach's own program settings in localStorage
    // (in-memory values are from client's CouchDB -- don't persist them)
    if (this._isManagingClient) {
      try {
        const existing = JSON.parse(localStorage.getItem('settings') || '{}');
        for (const key of programSettingKeys) {
          if (key in existing) {
            settingsToSave[key] = existing[key];
          } else {
            delete settingsToSave[key];
          }
        }
      } catch (e) {
        // the coach's own program settings are no longer protected: the client's values
        // below can overwrite them in this device's localStorage (#5191)
        this.reportProblem('SettingsCoachProtectionReadFailed', { error: describeErrorForLog(e) });
      }
    }

    localStorage.setItem('settings', JSON.stringify(settingsToSave));

    // sync callback is null during management (Phase 2a), so this is a no-op
    if (this._syncCallback && !skipDatabaseSync) {
      await this._syncCallback(this);
    }

    console.log('Settings: saved:', this);
  }

  async resetToDefaultsAsync(skipDatabaseSync: boolean = false) {
    for (const k in this._defaults) {
      this[k] = this._defaults[k];
    }

    console.log('Settings: reset to defaults', this);
    await this.saveAsync(skipDatabaseSync);
  }

  isClock24hr() {
    return (this.clockHourMode === ClockHourMode._24hr) || (this.clockHourMode === ClockHourMode.Automatic && this.locale.clock24hr);
  }

  isToastDismissed(toastId: string): boolean {
    const dismissedToasts = parseJSONSafely(this.permanentlyDismissedToastsJSON) || {};
    return dismissedToasts[toastId] === true;
  }

  async markToastDismissedAsync(toastId: string): Promise<void> {
    const dismissedToasts = parseJSONSafely(this.permanentlyDismissedToastsJSON) || {};
    dismissedToasts[toastId] = true;
    this.permanentlyDismissedToastsJSON = JSON.stringify(dismissedToasts);
    await this.saveAsync();
  }

  /**
   * #4625 — remember that we have just asked this person to rate the app.
   *
   * Written whether or not the operating system actually showed anything,
   * because neither store tells us: there is no callback, no rating, and no way
   * to find out whether the prompt appeared. Counting the attempt is the only
   * thing we CAN count, and it is the right thing to count anyway — the cap is
   * there to stop us pestering somebody, and a quota-suppressed ask still used
   * up the moment we chose.
   */
  async recordReviewPromptAskedAsync(now: DateTime = DateTime.utc()): Promise<void> {
    assertUsableReviewPromptMoment(now, 'recordReviewPromptAskedAsync');
    const askedAt = now.toUTC().toISO();
    // the list is written as a UNION with the old field, never as an append: a
    // phase 1 device may have recorded an earlier ask on the old field only,
    // and overwriting that field below would otherwise erase it from both places
    this.reviewPromptAskedAt_utc = mergeReviewPromptAskLists(
      this.reviewPromptAskedAt_utc, [this.reviewPromptLastAskedAt_utc, askedAt]);
    this.reviewPromptLastAskedAt_utc = mergeReviewPromptTimestamp(this.reviewPromptLastAskedAt_utc, askedAt);
    await this.saveAsync();
  }

  /**
   * #4625 — remember that this person told us something was wrong.
   *
   * Recorded locally because the app cannot read its own telemetry back, so the
   * events these routes already emit are no use to the gate. Called from BOTH
   * reporting routes — the profile page's report-a-problem action and the Send
   * Feedback page's Issue and Dislike kinds — because watching only one of them
   * would let somebody who complained yesterday straight through the gate.
   */
  async recordProblemReportedAsync(now: DateTime = DateTime.utc()): Promise<void> {
    assertUsableReviewPromptMoment(now, 'recordProblemReportedAsync');
    this.reviewPromptLastProblemReportedAt_utc = mergeReviewPromptTimestamp(
      this.reviewPromptLastProblemReportedAt_utc, now.toUTC().toISO());
    await this.saveAsync();
  }

  /** When we last asked for a rating, or `undefined` if we never have. */
  getReviewPromptLastAskedAt(): DateTime | undefined {
    return readReviewPromptTimestamp(this.reviewPromptLastAskedAt_utc);
  }

  /** #4816 — every ask we know of, from the list and the old single field, oldest first. */
  getReviewPromptAsks(): DateTime[] {
    return readReviewPromptAsks(this.reviewPromptAskedAt_utc, this.reviewPromptLastAskedAt_utc);
  }

  /** When they last told us something was wrong, or `undefined` if they never have. */
  getReviewPromptLastProblemReportedAt(): DateTime | undefined {
    return readReviewPromptTimestamp(this.reviewPromptLastProblemReportedAt_utc);
  }

  /** How many toasts has the user permanently hidden with "Never show again"? */
  dismissedToastCount(): number {
    return Object.keys(parseJSONSafely(this.permanentlyDismissedToastsJSON) || {}).length;
  }

  /** Bring back every toast the user permanently hid with "Never show again".
   *
   * "Never show again" is otherwise a one-way door: there was no way, anywhere in the app, to undo
   * it. That is how an account can end up with the offline toast switched off and no memory of ever
   * doing it. Exposed in Settings (test mode for now). */
  async resetDismissedToastsAsync(): Promise<void> {
    this.permanentlyDismissedToastsJSON = '';
    await this.saveAsync();
  }

  // get those settings that are relevant for telemetry
  getSettingsForTelemetry(): { [key: string]: any } {
    return {

      clockHourMode: this.clockHourMode,
      nutritionLabelStyle: this.nutritionLabelStyle,
      dateFormat: this.dateFormat,

      cardAnimations: this.cardAnimations,

      cachedRole: this.cachedRole,

      unitsPrefs: parseJSONSafely(this.unitsPrefsJSON),

      localeName: this.locale.localeName,
      decimalSeparator: this.locale.decimalSeparator,
      decimalSeparatorOverride: this.locale.decimalSeparatorOverride,

      usedCommunityBefore: this.usedCommunityBefore,

      showReportBugFAB: this.showReportBugFAB2,

      barcodeCameraId: this.barcodeCameraId,
      barcodeCameraFunctional: this.barcodeCameraFunctional,

      showDayStartEvents: this.showDayStartEvents,
      showDayEndEvents: this.showDayEndEvents,

      headerScrollHide: this.headerScrollHide,
      footerScrollHide: this.footerScrollHide,

      // homepage customization
      homepageShowWeighIn: this.homepageShowWeighIn,
      homepageShowWorkout: this.homepageShowWorkout,
      homepageShowMeals: this.homepageShowMeals,
      homepageShowGoalCard: this.homepageShowGoalCard,
      homepageShowBodyComposition: this.homepageShowBodyComposition,
      homepageShowNutritionChart: this.homepageShowNutritionChart,
      homepageShowCoachingTips: this.homepageShowCoachingTips,
    };
  }
}
